/**
 * Determines if Step 3 (seat selection) is needed based on cart contents and ticket types
 *
 * @param {Array} cart - Current cart items
 * @param {Array} ticketTypes - Available ticket types
 * @param {boolean} [delayedSeatAssignmentEnabled=false] - Force skip seat step when enabled
 * @returns {boolean} - True if Step 3 is needed, false if it should be skipped
 */
export function isStep3Needed(cart, ticketTypes, delayedSeatAssignmentEnabled = false) {
    if (!cart || !ticketTypes || cart.length === 0) {
        return false;
    }
    if (delayedSeatAssignmentEnabled) {
        return false;
    }

    // Get ticket types that are in the cart
    const cartTicketTypeIds = new Set(cart.map((item) => item.ticketTypeId));
    const relevantTicketTypes = ticketTypes.filter((type) => cartTicketTypeIds.has(type.id));

    // Check if any ticket type has seats and needs user interaction
    const needsUserInteraction = relevantTicketTypes.some((ticketType) => {
        // If ticket type has no seats, no interaction needed
        if (!ticketType.seats || ticketType.seats.length === 0) {
            return false;
        }

        // Keeping it here, if we ever decide to hide all auto seating as well
        /*if (ticketType.automaticSeating === 'fullauto') {
            return false;
        }*/

        // Manual or auto seating requires user interaction
        return true;
    });

    return needsUserInteraction;
}

export function isCartOnlyFullAuto(cart, ticketTypes) {
    if (!cart || !ticketTypes || cart.length === 0) return false;
    return cart.every((item) => {
        const ticketType = ticketTypes.find((tt) => tt.id === item.ticketTypeId);
        return ticketType?.automaticSeating === 'fullauto';
    });
}

/**
 * Gets the next step number, accounting for Step 3 skipping
 *
 * @param {number} currentStep - Current step number
 * @param {Array} cart - Current cart items
 * @param {Array} ticketTypes - Available ticket types
 * @returns {number} - Next step number
 */
export function getNextStep(currentStep, cart, ticketTypes, delayedSeatAssignmentEnabled = false) {
    if (currentStep === 2 && !isStep3Needed(cart, ticketTypes, delayedSeatAssignmentEnabled)) {
        return 4; // Skip Step 3
    }
    return currentStep + 1;
}

/**
 * Gets the previous step number, accounting for Step 3 skipping
 *
 * @param {number} currentStep - Current step number
 * @param {Array} cart - Current cart items
 * @param {Array} ticketTypes - Available ticket types
 * @returns {number} - Previous step number
 */
export function getPreviousStep(
    currentStep,
    cart,
    ticketTypes,
    delayedSeatAssignmentEnabled = false,
) {
    if (currentStep === 4 && !isStep3Needed(cart, ticketTypes, delayedSeatAssignmentEnabled)) {
        return 2; // Skip Step 3
    }
    return currentStep - 1;
}
